import type { AppRuntime } from './app-runtime.js';
import type {
    MatchUiBridgeV2,
    MatchUiCommandBusV2,
    MatchUiCommandResult,
    MatchUiSnapshotV2
} from './contracts/match-ui-v2.js';

function toResult<TPayload = undefined>(
    ok: boolean,
    reason: string,
    payload?: TPayload
): MatchUiCommandResult<TPayload> {
    return { ok, reason, payload };
}

export function createMatchUiCommandBusV2(runtime: AppRuntime): MatchUiCommandBusV2 {
    return {
        async togglePauseOrStart() {
            const result = await runtime.matchTogglePauseOrStart();
            return toResult(result.ok, result.reason);
        },
        async cycleSpeed() {
            const result = await runtime.matchCycleSpeed();
            return toResult(result.ok, result.reason, result.speed ? { speed: result.speed } : undefined);
        },
        async toggleAudio() {
            const result = await runtime.matchToggleAudio();
            return toResult(result.ok, result.reason, typeof result.muted === 'boolean' ? { muted: result.muted } : undefined);
        },
        async cycleCamera() {
            const result = await runtime.matchCycleCamera();
            return toResult(result.ok, result.reason, result.cameraMode ? { cameraMode: result.cameraMode } : undefined);
        },
        async setFormation(formation) {
            const result = await runtime.matchSetFormation(formation);
            return toResult(result.ok, result.reason);
        },
        async setTactic(name, value) {
            const result = await runtime.matchSetTactic(name, value);
            return toResult(result.ok, result.reason);
        },
        async togglePlayerHighlight(playerId) {
            const result = await runtime.matchTogglePlayerHighlight(playerId);
            return toResult(result.ok, result.reason);
        },
        async togglePlayerDetails(playerId) {
            const result = await runtime.matchTogglePlayerDetails(playerId);
            return toResult(result.ok, result.reason);
        },
        async makeSubstitution(outPlayerId, inPlayerId) {
            const result = await runtime.matchMakeSubstitution(outPlayerId, inPlayerId);
            return toResult(result.ok, result.reason);
        },
        async returnToHub() {
            const result = await runtime.managerBackToHub().then(() => ({ ok: true, reason: 'Returned to manager hub.' }))
                .catch((error) => ({
                    ok: false,
                    reason: error instanceof Error && error.message ? error.message : 'Failed to return to manager hub.'
                }));
            return toResult(result.ok, result.reason);
        }
    };
}

export function createMatchUiBridgeV2(runtime: AppRuntime): MatchUiBridgeV2 {
    return {
        getSnapshot(): MatchUiSnapshotV2 {
            return runtime.getMatchUiSnapshot();
        },
        subscribe(listener) {
            return runtime.subscribeMatchUi(listener);
        },
        commands: createMatchUiCommandBusV2(runtime)
    };
}
